/**
 * Coding API Service - Cliente HTTP para el sistema de codificación de objetos
 *
 * @version 1.0
 * @updated 2026-02-18
 * @module core/coding
 *
 * @description
 * Cliente Axios para los endpoints del módulo de codificación.
 * Genera, verifica y registra cambios de código de objetos en el mapa.
 *
 * @remarks
 * Endpoints utilizados:
 * - POST /coding/generate  → Genera código para objeto nuevo
 * - POST /coding/check-recode  → Verifica si mover cambia el código (read-only)
 * - POST /coding/record-recode → Confirma y registra el cambio de código
 */

import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

/**
 * Obtiene el token de autenticación del localStorage
 */
const getAuthToken = (): string | null => {
  const authStorage = localStorage.getItem('auth-storage');
  if (!authStorage) return null;
  const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
  return parsed.state?.token || null;
};

const authHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ─── DTOs ────────────────────────────────────────────────────────────────────

/**
 * DTO para generar un código de objeto nuevo con codificación geoespacial
 *
 * @remarks
 * El código generado incluye:
 * - Tipo de objeto (2 letras)
 * - Código de celda H3 (5 caracteres)
 * - Secuencia en la celda (3 dígitos)
 * - Fecha de creación (DDMMYYYY)
 *
 * @example
 * Resultado: "TR_AA001_005_25022026"
 */
export interface GenerateCodeDto {
  /** Tipo de objeto (e.g. "TR" para árbol) */
  objectType: string;
  /** Latitud del objeto */
  lat: number;
  /** Longitud del objeto */
  lng: number;
  /** ID del proyecto */
  projectId: string;
}

/**
 * DTO para verificar si mover un objeto cambiará su código
 */
export interface CheckRecodeDto {
  /** Tipo de objeto (e.g. "TR") */
  objectType: string;
  /** Código actual del objeto */
  currentCode: string;
  /** Nueva latitud */
  newLat: number;
  /** Nueva longitud */
  newLng: number;
  /** ID del proyecto */
  projectId: string;
}

/**
 * DTO para confirmar y registrar un cambio de código
 */
export interface RecordRecodeDto {
  /** ID del objeto en su tabla de dominio */
  objectId: string;
  /** Tipo de objeto (e.g. "TR") */
  objectType: string;
  /** Código actual del objeto */
  currentCode: string;
  /** Nueva latitud */
  newLat: number;
  /** Nueva longitud */
  newLng: number;
  /** ID del proyecto */
  projectId: string;
  /** Razón del cambio */
  reason?: string;
}

// ─── Respuestas ───────────────────────────────────────────────────────────────

/**
 * Resultado de generación de código
 */
export interface GenerateCodeResult {
  /** Código compuesto con fecha (e.g. "TR_AA001_005_25022026") */
  code: string;
  /** Código de celda (e.g. "AA-001") */
  cellCode: string;
  /** H3 index de la celda */
  h3Index: string;
  /** ID de la malla canónica utilizada */
  gridId: string;
  /** Número de secuencia asignado */
  sequence: number;
  /** Fecha de creación (formato DDMMYYYY) */
  creationDate: string;
}

/**
 * Resultado de verificación de re-codificación
 */
export interface CheckRecodeResult {
  /** true si el objeto cambiaría de celda al mover */
  willRecode: boolean;
  /** Código actual del objeto */
  currentCode: string;
  /** Código tentativo nuevo (informativo, no reservado) */
  tentativeNewCode: string | null;
  /** Celda actual */
  currentCellCode: string;
  /** Nueva celda (si cambia) */
  newCellCode: string | null;
}

/**
 * Resultado de registro de cambio de código
 */
export interface RecordRecodeResult {
  /** Nuevo código definitivo */
  newCode: string;
  /** Código anterior */
  previousCode: string;
  /** Nueva celda */
  newCellCode: string;
}

// ─── Cliente API ──────────────────────────────────────────────────────────────

/**
 * Cliente API para el sistema de codificación de objetos
 */
export const codingApi = {
  /**
   * Genera un nuevo código para un objeto recién creado en el mapa.
   *
   * @param dto - Tipo, coordenadas y proyecto
   * @returns Código generado y metadatos de la celda
   */
  async generateCode(dto: GenerateCodeDto): Promise<GenerateCodeResult> {
    const response = await axios.post<GenerateCodeResult>(
      `${API_URL}/coding/generate`,
      dto,
      { headers: authHeaders() },
    );
    return response.data;
  },

  /**
   * Verifica si mover un objeto a nuevas coordenadas cambiará su código.
   *
   * @remarks
   * Operación de solo lectura: no incrementa contadores ni reserva códigos.
   *
   * @param dto - Tipo, código actual, nuevas coordenadas y proyecto
   * @returns Estado de re-codificación con código tentativo
   */
  async checkRecode(dto: CheckRecodeDto): Promise<CheckRecodeResult> {
    const response = await axios.post<CheckRecodeResult>(
      `${API_URL}/coding/check-recode`,
      dto,
      { headers: authHeaders() },
    );
    return response.data;
  },

  /**
   * Confirma y registra el cambio de código de un objeto movido.
   *
   * @remarks
   * Llamar solo cuando el usuario confirmó el guardado.
   * Incrementa el contador de secuencia y registra en el historial.
   *
   * @param dto - Objeto, código actual, nuevas coordenadas y proyecto
   * @returns Nuevo código definitivo y código anterior
   */
  async recordRecode(dto: RecordRecodeDto): Promise<RecordRecodeResult> {
    const response = await axios.post<RecordRecodeResult>(
      `${API_URL}/coding/record-recode`,
      dto,
      { headers: authHeaders() },
    );
    return response.data;
  },
};
